import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { ReleaseListRow } from './ReleaseListRow';
import { QuietReleases } from './QuietReleases';
import { ReleaseEntry, ReleaseEntryGroups } from './releaseListEntries';
import { releaseListMessages } from './releaseListMessages';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './ReleasesList.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReleasesListBodyProps {
  groups: ReleaseEntryGroups;
  now: Date;
  /** The "Find a song" text: a search with no match says so; a search opens Quiet. */
  query: string;
}

/** The head row, then Drafts, Upcoming, Active and Quiet (design a1). A group with no song is left out. */
const ReleasesListBodyComponent: React.FC<ReleasesListBodyProps> = ({ groups, now, query }) => {
  const intl = useIntl();
  const total = groups.drafts.length + groups.upcoming.length + groups.active.length + groups.quiet.length;
  const renderRows = (entries: ReleaseEntry[], primaryFirst: boolean): React.ReactNode =>
    entries.map(({ item, row }, index) => (
      <ReleaseListRow key={item.uuid} item={item} row={row} now={now} isPrimary={primaryFirst && index === 0} />
    ));

  if (total === 0) {
    return (
      <div className={styles.noMatch}>{intl.formatMessage(releaseListMessages.noMatch, { query: query.trim() })}</div>
    );
  }

  return (
    <>
      <div className={styles.listHead} aria-hidden="true">
        <span />
        <span>{intl.formatMessage(releaseListMessages.colSong)}</span>
        <span>{intl.formatMessage(releaseListMessages.colNext)}</span>
        <span>{intl.formatMessage(releaseListMessages.colAssets)}</span>
        <span>{intl.formatMessage(releaseListMessages.colPosts)}</span>
        <span>{intl.formatMessage(releaseListMessages.colLinks)}</span>
        <span />
      </div>
      {groups.drafts.length > 0 && (
        <>
          <div className={styles.sect}>
            <h4 className={styles.sectTitle}>{intl.formatMessage(releaseListMessages.drafts)}</h4>
            <span className={styles.sectSub}>{intl.formatMessage(releaseListMessages.draftsSub)}</span>
          </div>
          {renderRows(groups.drafts, false)}
        </>
      )}
      {groups.upcoming.length > 0 && (
        <>
          <div className={styles.sect}>
            <h4 className={styles.sectTitle}>{intl.formatMessage(releaseListMessages.upcoming)}</h4>
            <span className={styles.sectSub}>
              {intl.formatMessage(releaseListMessages.upcomingSub, { count: groups.upcoming.length })}
            </span>
          </div>
          {renderRows(groups.upcoming, true)}
        </>
      )}
      {groups.active.length > 0 && (
        <>
          <div className={styles.sect}>
            <h4 className={styles.sectTitle}>{intl.formatMessage(releaseListMessages.active)}</h4>
            <span className={styles.sectSub}>{intl.formatMessage(releaseListMessages.activeSub)}</span>
          </div>
          {renderRows(groups.active, false)}
        </>
      )}
      {groups.quiet.length > 0 && <QuietReleases entries={groups.quiet} now={now} forceOpen={query.trim() !== ''} />}
    </>
  );
};

export const ReleasesListBody = TypedMemo(ReleasesListBodyComponent);
